import React, { useEffect } from 'react';
import { View } from 'react-native';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchBoitierStatus } from '../redux/slices/boitierStatusSlice';

interface BoitierStatusBadgeProps {
  imei?: string;
  /** Diameter of the colored status dot. */
  size?: number;
}

const REFRESH_DELAY_MS = 60_000;
/** Width of the soft halo around the dot (keeps any provider color readable on both themes). */
const HALO = 3;

export default function BoitierStatusBadge({ imei, size = 10 }: BoitierStatusBadgeProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const normalizedImei = imei?.trim();
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) =>
    normalizedImei ? state.boitierStatus.byImei[normalizedImei] : undefined
  );

  useEffect(() => {
    if (!normalizedImei) return;

    dispatch(fetchBoitierStatus(normalizedImei));
    const intervalId = setInterval(() => {
      dispatch(fetchBoitierStatus(normalizedImei));
    }, REFRESH_DELAY_MS);

    return () => clearInterval(intervalId);
  }, [dispatch, normalizedImei]);

  // Data-driven color: `colorHex` comes from the GPS provider (device status), not from the design system.
  const dotColor = status?.colorHex ?? colors.border;
  const outer = size + HALO * 2;

  return (
    <View
      style={[styles.halo, { width: outer, height: outer, borderRadius: outer / 2 }]}
      accessibilityRole="image"
      accessibilityLabel={status?.status ? `Statut du boîtier : ${status.status}` : 'Statut du boîtier inconnu'}
    >
      <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: dotColor }} />
    </View>
  );
}

const useStyles = makeStyles((c) => ({
  halo: {
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: c.surfaceAlt,
    borderWidth: 1,
    borderColor: c.divider,
  },
}));
